import { defineComponent as _defineComponent } from 'vue'
import {
  PropType, ref, Ref, watch, onMounted, computed, provide, onBeforeMount, nextTick,
} from 'vue';
import { PaymentWizardMode, PaymentWizardStep, PaymentWizardStepKey } from '@/services/payment-wizard-utils';
import {
  Customer, Invoice, Price, SetupIntent,
} from '@/api/api-billing';
import i18n from '@/plugins/i18n';
import store from '@/store/store';
import BillingDetails from './PaymentBillingDetails.vue';
import PaymentDetails, { PaymentFinishedEvent } from './PaymentDetails.vue';
import PaymentSuccess from './PaymentSuccess.vue';
import PlanSelector, { PlanSelectedEvent } from './PlanSelector.vue';
import PaymentMethods, { NewPaymentMethodEvent } from './PaymentMethods.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentWizard',
  props: {
  mode: { type: String as PropType<PaymentWizardMode>, default: undefined },

  price: { type: Object as PropType<Price|undefined>, default: undefined },
  previewInvoice: { type: Object as PropType<Invoice|undefined>, default: undefined },

  invoice: { type: Object as PropType<Invoice|undefined>, default: undefined },

  finishedInvoice: { type: Object as PropType<Invoice|undefined>, default: undefined },
  finishedIntent: { type: Object as PropType<SetupIntent|undefined>, default: undefined },

  setupIntent: { type: Object as PropType<SetupIntent|undefined>, default: undefined },

  prices: { type: Array as PropType<Array<Price>|undefined>, default: undefined },
},
  emits: ['close'],
  setup(__props, { emit }) {

const props = __props;



const paymentDetailsPrice = ref(undefined) as Ref<Price|undefined>;
const paymentDetailsPreviewInvoice = ref(undefined) as Ref<Invoice|undefined>;
const paymentDetailsInvoice = ref(undefined) as Ref<Invoice|undefined>;
const paymentDetailsPrices = ref(undefined) as Ref<Price[]|undefined>;
const paymentSuccessInvoice = ref(undefined) as Ref<Invoice|undefined>;
const paymentSuccessIntent = ref(undefined) as Ref<SetupIntent|undefined>;
const paymentMethodsClientSecret = ref(undefined) as Ref<string|undefined>;



const stepKeys = ref([]) as Ref<PaymentWizardStepKey[]>;

const steps = ref([]) as Ref<PaymentWizardStep[]>;
const disabledSteps = ref([]) as Ref<string[]>;
const completedSteps = ref([]) as Ref<string[]>;
const editableSteps = ref([]) as Ref<string[]>;

const stepNo = ref(1) as Ref<number>;
const step = computed(() => steps.value[stepNo.value - 1]);

const stepMap = new Map<string, PaymentWizardStep>();
const addStep = (s: PaymentWizardStep) => {
  stepMap.set(s.key, s);
  steps.value = stepKeys.value.flatMap((sk) => stepMap.get(sk) ?? []);
};
provide('addStep', addStep);

const stepChanged = (s: PaymentWizardStep) => {
  // update step in steps
  stepMap.set(s.key, s);
  steps.value = stepKeys.value.flatMap((sk) => stepMap.get(sk) ?? []);

  // enable/disable step
  if (s.complete && disabledSteps.value.includes(s.key)) {
    disabledSteps.value = disabledSteps.value.filter((dsk) => dsk !== s.key);
  }
  if (!s.complete && !disabledSteps.value.includes(s.key)) {
    disabledSteps.value = [...disabledSteps.value, s.key];
  }
};
provide('stepChanged', stepChanged);

const submit = async () => {
  // submit step and
  const submitAction = step.value.submitAction ?? null;
  if (submitAction) {
    const res = await submitAction();
    // eslint-disable-next-line no-use-before-define
    const ok = handleResult(res, step.value.key);
    if (!ok) return false;
  }

  // set step as completed
  if (!completedSteps.value.includes(step.value!.key)) completedSteps.value.push(step.value!.key);

  // last step -> close wizard
  if (stepNo.value === stepKeys.value.length) { emit('close'); return true; }

  // handleResult to prepareAction reactivity fix
  await nextTick();

  // prepare the next step
  const prepareAction = (stepNo.value < stepKeys.value.length) && steps.value[stepNo.value].prepareAction;
  if (prepareAction) {
    const ok = await prepareAction();
    if (!ok) return false;
  }

  // move to the next step
  stepNo.value += 1;
  return true;
};
const submitStep = (s: PaymentWizardStep) => {
  if (s.key !== step.value.key) { return; }
  submit();
};
provide('submitStep', submitStep);

const cancel = async () => {
  const cancelAction = step.value.cancelAction ?? null;
  if (cancelAction) await cancelAction();

  emit('close');
};

const submitText = computed(
  () => step.value?.submitText
  ?? (stepNo.value < stepKeys.value.length ? i18n.tc('button.continue') : i18n.tc('button.ok')),
);

// watch(
//   () => steps.value,
//   (value) => { console.log('steps array changed: ', value); },
// );

const handleResult = (result: any, key: string) => {
  console.log('handleResult: ', result);

  if (!result) return false;

  if (key === PaymentWizardStepKey.PlanSelector) {
    const event = result as PlanSelectedEvent;
    paymentDetailsPrice.value = event.price;
    paymentDetailsPreviewInvoice.value = event.previewInvoice;
  } else if (key === PaymentWizardStepKey.PaymentDetails) {
    const event = result as PaymentFinishedEvent;
    paymentSuccessInvoice.value = event.finishedInvoice;
    paymentSuccessIntent.value = event.finishedSetupIntent;
  } else if (key === PaymentWizardStepKey.NewPaymentMethod) {
    const event = result as NewPaymentMethodEvent;
    paymentSuccessIntent.value = event.finishedSetupIntent;
  }

  return true;
};

// create steps
const createSteps = () => {
  const keys = [] as PaymentWizardStepKey[];

  // subscription only
  if ([PaymentWizardMode.Subscription].includes(props.mode) && !props.price) {
    keys.push(PaymentWizardStepKey.PlanSelector);
  }

  // billing details or no customer -> create customer details
  const customer = store.getters.billingCustomer as Customer;
  if ([PaymentWizardMode.BillingDetails].includes(props.mode)
  || ([PaymentWizardMode.Subscription, PaymentWizardMode.Invoice, PaymentWizardMode.Prices].includes(props.mode)
    && !customer)
  ) {
    keys.push(PaymentWizardStepKey.BillingDetails);
  }

  // payment details and
  if ([PaymentWizardMode.Subscription, PaymentWizardMode.Invoice, PaymentWizardMode.Prices].includes(props.mode)) {
    keys.push(PaymentWizardStepKey.PaymentDetails);
  }

  // new payment method
  if (props.mode === PaymentWizardMode.NewPaymentMethod) {
    keys.push(PaymentWizardStepKey.NewPaymentMethod);
  }

  // success
  if ([PaymentWizardMode.Subscription, PaymentWizardMode.Invoice,
    PaymentWizardMode.NewPaymentMethod, PaymentWizardMode.Result, PaymentWizardMode.Prices].includes(props.mode)) {
    keys.push(PaymentWizardStepKey.PaymentSuccess);
  }

  stepKeys.value = keys;
  console.log('createSteps: ', keys);
};

onBeforeMount(() => {
  createSteps();

  paymentDetailsPrice.value = props.price;
  paymentDetailsPreviewInvoice.value = props.previewInvoice;
  paymentDetailsInvoice.value = props.invoice;
  paymentDetailsPrices.value = props.prices;
  paymentSuccessInvoice.value = props.finishedInvoice;
  paymentSuccessIntent.value = props.finishedIntent;
  paymentMethodsClientSecret.value = props.setupIntent?.clientSecret;
});

onMounted(async () => {
  const { prepareAction } = step.value;
  if (prepareAction) {
    const ok = await prepareAction();
    if (!ok) cancel();
  }
});


return { __sfc: true,props, paymentDetailsPrice, paymentDetailsPreviewInvoice, paymentDetailsInvoice, paymentDetailsPrices, paymentSuccessInvoice, paymentSuccessIntent, paymentMethodsClientSecret, emit, stepKeys, steps, disabledSteps, completedSteps, editableSteps, stepNo, step, stepMap, addStep, stepChanged, submit, submitStep, cancel, submitText, handleResult, createSteps, PaymentWizardStepKey, BillingDetails, PaymentDetails, PaymentSuccess, PlanSelector, PaymentMethods }
}

})